import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useSyncConnectors } from '../../hooks/useSyncConnectors';
import type { UserData } from '../../types/user';

interface UserFormDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    user: UserData | null;
    validationErrors: Record<string, string[]> | null;
    onSave: (data: Record<string, unknown>) => Promise<void>;
}

export function UserFormDialog({ open, onOpenChange, user, validationErrors, onSave }: UserFormDialogProps) {
    const { connectors, loading: connectorsLoading, error: connectorsError } = useSyncConnectors(open);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState<'admin' | 'user'>('user');
    const [connectorId, setConnectorId] = useState<number | null>(null);
    const [connectorIds, setConnectorIds] = useState<number[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (user) {
            setName(user.name);
            setEmail(user.email);
            setPassword('');
            setRole(user.role);
            setConnectorId(user.connector_id);
            setConnectorIds(user.connectors.map((c) => c.connector_id));
        } else {
            setName('');
            setEmail('');
            setPassword('');
            setRole('user');
            const fallback = connectors[0]?.connector_id ?? null;
            setConnectorId(fallback);
            setConnectorIds(fallback !== null ? [fallback] : []);
        }
    }, [open, user, connectors]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const data: Record<string, unknown> = {
                name,
                email,
                role,
                connector_id: connectorId,
                connector_ids: connectorIds,
            };
            if (password) data.password = password;
            else if (!user) data.password = password; // required for create
            await onSave(data);
        } finally {
            setSaving(false);
        }
    };

    const fieldError = (field: string) =>
        validationErrors?.[field]?.[0];

    const toggleConnector = (id: number) => {
        setConnectorIds((prev) =>
            prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
        );
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                        <Input value={name} onChange={(e) => setName(e.target.value)} required />
                        {fieldError('name') && <p className="text-sm text-red-600 mt-1">{fieldError('name')}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                        {fieldError('email') && <p className="text-sm text-red-600 mt-1">{fieldError('email')}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mot de passe {user && <span className="text-gray-400">(laisser vide pour ne pas changer)</span>}
                        </label>
                        <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} {...(!user ? { required: true } : {})} minLength={8} />
                        {fieldError('password') && <p className="text-sm text-red-600 mt-1">{fieldError('password')}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                        <select
                            value={role}
                            onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                        >
                            <option value="user">Utilisateur</option>
                            <option value="admin">Administrateur</option>
                        </select>
                        {fieldError('role') && <p className="text-sm text-red-600 mt-1">{fieldError('role')}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Société principale</label>
                        <select
                            value={connectorId ?? ''}
                            onChange={(e) => setConnectorId(Number(e.target.value))}
                            disabled={connectorsLoading || connectors.length === 0}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-400"
                        >
                            {connectorsLoading && <option value="">Chargement...</option>}
                            {!connectorsLoading && connectors.length === 0 && <option value="">Aucune société</option>}
                            {connectors.map((c) => (
                                <option key={c.connector_id} value={c.connector_id}>{c.company_name}</option>
                            ))}
                        </select>
                        {connectorsError && <p className="text-sm text-red-600 mt-1">Erreur de chargement des sociétés : {connectorsError}</p>}
                        {fieldError('connector_id') && <p className="text-sm text-red-600 mt-1">{fieldError('connector_id')}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Accès sociétés</label>
                        <div className="space-y-2">
                            {connectorsLoading && <p className="text-sm text-gray-400">Chargement...</p>}
                            {!connectorsLoading && connectors.length === 0 && <p className="text-sm text-gray-400">Aucune société</p>}
                            {connectors.map((c) => (
                                <label key={c.connector_id} className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={connectorIds.includes(c.connector_id)}
                                        onChange={() => toggleConnector(c.connector_id)}
                                        className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                                    />
                                    {c.company_name}
                                </label>
                            ))}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Enregistrement...' : user ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
